// apps/web/src/components/analytics/occupancy-heatmap.tsx
import {
  type OccupancyBookingSpan,
  type OccupancySeasonHeatmapCell,
  type OccupancyWeekPrice,
  roundBookedWeeks,
} from '@sea-snag/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@sea-snag/ui';
import { CalendarDays } from 'lucide-react';
import { useRef, useState } from 'react';

import { cellPriceLabel, NO_PRICE_MARK, SOLD_SPAN_KINDS } from '../../lib/cell-price';
import { moveGridFocus } from '../../lib/grid-focus';
import {
  formatCharterWeekRange,
  WEEK_COUNT,
  weekMidForColumn,
  weekStartForColumn,
} from '../../lib/heatmap-columns';
import { cellKind, cellStyle, LEGEND_ORDER, MARKED_KINDS } from '../../lib/occupancy-cell';
import { buildHeatmapRows } from '../../lib/occupancy-rows';
import { spanLengthDays, spansInWeek } from '../../lib/occupancy-spans';
import { resolveRowSpans } from '../../lib/row-spans';

interface OccupancyHeatmapProps {
  year: string;
  boats: Array<{ pid: string; name: string }>;
  heatmap: OccupancySeasonHeatmapCell[];
  spans: OccupancyBookingSpan[];
  /** List price per boat and charter week, printed in the cells. */
  weekPrices: OccupancyWeekPrice[];
  isInSeasonColumn: (weekStart: string) => boolean;
}

const DAY_FMT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});
const fmtDay = (iso: string) => DAY_FMT.format(new Date(`${iso}T00:00:00Z`));

/** `DD.MM.YY` — the column heading MMK's booking sheet uses. */
function fmtWeekHeader(isoDate: string): string {
  const [y, m, d] = isoDate.split('-');
  return `${d}.${m}.${y!.slice(2)}`;
}

/**
 * The per-boat weekly grid.
 *
 * Cells are Saturday-to-Saturday charter weeks, one solid colour each: the week
 * either held a booking of that type or it did not. How much of it was sold is in
 * the cell detail, not in a shade.
 *
 * Two views. Full year is the default and shows all 53 columns with the season
 * shaded; "Season only" hides the columns outside it. Switching is presentation
 * alone — the booked-weeks figure beside each boat is the same number either way,
 * because it is the one that has to agree with Season Progress.
 *
 * The whole grid is a single tab stop. Arrow keys move, Enter or Space opens the
 * detail; making every cell tabbable put roughly fifteen hundred stops between
 * the grid and the page below it.
 *
 * The grid scrolls inside a box no taller than most of the screen, with the
 * week row and the boat names pinned: further into the season it was hard to
 * tell which boat a cell belonged to, and the sideways scrollbar sat below the
 * last boat (#255).
 */
export function OccupancyHeatmap({
  year,
  boats,
  heatmap,
  spans,
  weekPrices,
  isInSeasonColumn,
}: OccupancyHeatmapProps) {
  const selectedYear = Number(year);
  // The season is what charter managers look at; the off-season weeks are
  // mostly empty and push the sold ones off the right of the screen. Full year
  // is one click away.
  const [seasonOnly, setSeasonOnly] = useState(true);
  const [focus, setFocus] = useState({ row: 0, col: 0 });
  const [open, setOpen] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Keyed per boat and week so a cell can print its own price.
  const priceByCell = new Map<string, OccupancyWeekPrice>();
  for (const p of weekPrices) priceByCell.set(`${p.boatPid}|${p.weekStart}`, p);
  const priceFmt = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });

  // Row order comes from the caller, which owns the sort control. This component
  // used to re-sort by booked weeks here, which overrode that control silently.
  const rows = buildHeatmapRows(boats, heatmap, isInSeasonColumn, selectedYear);

  // Which columns are drawn. Season-only narrows the view; it never touches the
  // totals, which stay whole-year so they keep matching Season Progress.
  const columns = Array.from({ length: WEEK_COUNT }, (_, i) => i).filter(
    (i) => !seasonOnly || isInSeasonColumn(weekMidForColumn(selectedYear, i))
  );

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const next = moveGridFocus(focus, e.key, { rows: rows.length, cols: columns.length });
    if (!next) return;
    e.preventDefault();
    setFocus(next);
    const row = rows[next.row];
    if (!row) return;
    // A booking spanning several weeks renders one cell, so the columns it
    // covers have no element of their own. Walk left to the cell that owns the
    // target column rather than losing focus into a gap.
    for (let probe = next.col; probe >= 0; probe -= 1) {
      const col = columns[probe];
      if (col === undefined) continue;
      const el = gridRef.current?.querySelector<HTMLButtonElement>(
        `[data-cell="${row.boat.pid}-${col}"]`
      );
      if (el) {
        el.focus();
        return;
      }
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            Booked weeks · {year}
          </CardTitle>
          <CardDescription>
            One cell per charter week, Saturday to Saturday.{' '}
            {seasonOnly ? 'Showing the season only.' : 'The shaded band marks the season.'}
          </CardDescription>
        </div>
        <div className="flex shrink-0 gap-1">
          <Button
            variant={seasonOnly ? 'ghost' : 'secondary'}
            size="sm"
            className="h-8 text-xs"
            aria-pressed={!seasonOnly}
            onClick={() => setSeasonOnly(false)}
          >
            Full year
          </Button>
          <Button
            variant={seasonOnly ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8 text-xs"
            aria-pressed={seasonOnly}
            onClick={() => setSeasonOnly(true)}
          >
            Season only
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="max-h-[70vh] overflow-auto">
          <div
            ref={gridRef}
            role="grid"
            aria-label={`Booked weeks per boat, ${year}`}
            onKeyDown={onKeyDown}
            className="grid items-center gap-y-2.5 text-xs"
            style={{
              gridTemplateColumns: `minmax(150px, 190px) repeat(${columns.length}, 62px) minmax(72px, auto)`,
            }}
          >
            <div className="bg-card sticky left-0 top-0 z-30 self-stretch" />
            {columns.map((col) => (
              <div
                key={`hdr-${col}`}
                className="text-muted-foreground bg-card sticky top-0 z-20 whitespace-nowrap py-1 text-center text-[10px]"
                aria-hidden
              >
                {/* The week's Saturday, as on a booking sheet. A month label
                    alone cannot identify a column once cells are wide enough
                    that the grid scrolls. */}
                {fmtWeekHeader(weekStartForColumn(selectedYear, col))}
              </div>
            ))}
            <div className="text-muted-foreground bg-card sticky top-0 z-20 py-1 pl-2 text-right text-[10px]">
              Booked
            </div>

            {rows.map(({ boat, inner, counts }, rowIndex) => {
              // One booking spanning several weeks is one bar, priced once.
              const rowSpans = resolveRowSpans(columns, selectedYear, spans, boat.pid);
              return (
                <div className="contents" key={boat.pid} role="row">
                  <div
                    className="bg-card sticky left-0 z-10 flex items-center self-stretch truncate pr-2 font-medium"
                    title={boat.name}
                  >
                    <span className="truncate">{boat.name}</span>
                  </div>
                  {columns.map((col, colIndex) => {
                    const cell = inner.get(col);
                    const kind = cellKind(cell);
                    const style = cellStyle(kind);
                    // The charter week, Saturday to Saturday, for every cell: an
                    // empty week used to show its Tuesday as a raw date (#274).
                    const weekRange = formatCharterWeekRange(weekStartForColumn(selectedYear, col));
                    const listPrice = priceByCell.get(
                      `${boat.pid}|${weekStartForColumn(selectedYear, col)}`
                    );
                    const spanInfo = rowSpans.get(col);
                    // A booking renders as one cell spanning its weeks, so the
                    // columns it covers render nothing of their own. Two touching
                    // cells still read as two bookings; one wide cell cannot.
                    if (spanInfo && !spanInfo.isSpanStart) return null;
                    // A booking prints what it is worth, an open week what we
                    // are asking for it, and a sold week with no price in MMK
                    // gets a mark rather than nothing — see cellPriceLabel.
                    const shown = cellPriceLabel({
                      ...(spanInfo
                        ? { span: { kind: spanInfo.kind, amount: spanInfo.amount } }
                        : {}),
                      listPrice: listPrice?.amount ?? null,
                      format: (n) => priceFmt.format(n),
                    });
                    const noPriceRecorded = shown === NO_PRICE_MARK;
                    const inSeason = isInSeasonColumn(weekMidForColumn(selectedYear, col));
                    const id = `${boat.pid}-${col}`;
                    // Only the focused cell is tabbable, so the grid is one stop.
                    // A spanning cell is focused when the focused column is any
                    // of the ones it covers.
                    const isFocused =
                      focus.row === rowIndex &&
                      focus.col >= colIndex &&
                      focus.col < colIndex + (spanInfo?.length ?? 1);
                    const hits = cell
                      ? spansInWeek(spans, cell.weekStart, cell.weekEnd, boat.pid)
                      : [];
                    const summary = `${weekRange} · ${style.label}`;

                    return (
                      <Popover
                        key={id}
                        open={open === id}
                        onOpenChange={(o) => setOpen(o ? id : null)}
                      >
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            data-cell={id}
                            role="gridcell"
                            style={
                              spanInfo && spanInfo.length > 1
                                ? { gridColumn: `span ${spanInfo.length}` }
                                : undefined
                            }
                            tabIndex={isFocused ? 0 : -1}
                            aria-label={`${boat.name}, ${summary}${
                              noPriceRecorded ? ', no price recorded' : ''
                            }`}
                            {...(noPriceRecorded
                              ? { title: 'This booking has no price in MMK' }
                              : {})}
                            onFocus={() => setFocus({ row: rowIndex, col: colIndex })}
                            className={cn(
                              // Scroll margins keep a cell focused from the
                              // keyboard clear of the pinned names and weeks.
                              'relative mx-[2px] flex h-7 w-auto scroll-ml-48 scroll-mt-8 items-center justify-center',
                              'rounded-[3px] border text-[11px] tabular-nums transition-colors',
                              'hover:border-foreground/30 focus:ring-ring focus:outline-none focus:ring-2',
                              inSeason && !seasonOnly ? 'border-primary/20' : 'border-transparent',
                              style.className,
                              // Each kind owns its text colour: a light fill such
                              // as the option's needs dark text, and the old
                              // blanket white is what prevented using one.
                              style.textClassName
                            )}
                          >
                            {shown}
                            {/* A regatta shares the charter colour, so a corner
                              marker is what distinguishes it. */}
                            {MARKED_KINDS.has(kind) && (
                              <span
                                aria-hidden
                                className="border-t-background absolute right-0 top-0 border-l-[5px] border-t-[5px] border-l-transparent"
                              />
                            )}
                          </button>
                        </PopoverTrigger>
                        {/* A Popover rather than a Tooltip: tooltips are unreliable
                          on touch, and this dismisses by tapping outside or with
                          the close control, as well as Escape. */}
                        <PopoverContent
                          side="top"
                          className="w-64 text-xs"
                          onOpenAutoFocus={(e) => e.preventDefault()}
                        >
                          <div className="space-y-2">
                            <div>
                              <div className="font-medium">{boat.name}</div>
                              <div className="text-muted-foreground">{weekRange}</div>
                            </div>
                            {hits.length === 0 ? (
                              <div className="text-muted-foreground">
                                {listPrice ? (
                                  <>
                                    Open — {priceFmt.format(listPrice.amount)} {listPrice.currency}
                                    {listPrice.listAmount != null && (
                                      <span className="block">
                                        {listPrice.discountPct}% off{' '}
                                        {priceFmt.format(listPrice.listAmount)} {listPrice.currency}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  'Free all week.'
                                )}
                              </div>
                            ) : (
                              <ul className="space-y-1.5">
                                {hits.map(({ span, daysInWeek }, i) => (
                                  <li
                                    key={`${span.kind}-${span.startDate}-${i}`}
                                    className="flex gap-2"
                                  >
                                    <span
                                      className={cn(
                                        'mt-0.5 h-3 w-3 shrink-0 rounded-[2px]',
                                        cellStyle(span.kind).className
                                      )}
                                      aria-hidden
                                    />
                                    <span>
                                      <span className="font-medium">
                                        {cellStyle(span.kind).label}
                                      </span>{' '}
                                      {/* The booking's own length. Quoting the
                                        days inside this week made a fortnight
                                        clicked on its first week read as a
                                        week, while the dates under it said
                                        otherwise. Options are not sold time, so
                                        they are not called booked days. */}
                                      — {spanLengthDays(span)}{' '}
                                      {span.kind === 'option' ? 'option day' : 'day'}
                                      {spanLengthDays(span) === 1 ? '' : 's'}
                                      {span.amount == null && SOLD_SPAN_KINDS.has(span.kind) && (
                                        <span className="text-muted-foreground block">
                                          No price recorded in MMK
                                        </span>
                                      )}
                                      <span className="text-muted-foreground block">
                                        {fmtDay(span.startDate)} → {fmtDay(span.endDate)}
                                        {/* Only worth saying when the booking
                                          runs past this week's edges. */}
                                        {spanLengthDays(span) !== daysInWeek &&
                                          ` · ${daysInWeek} in this week`}
                                      </span>
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-full text-xs"
                              onClick={() => setOpen(null)}
                            >
                              Close
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    );
                  })}
                  <div
                    className="text-muted-foreground whitespace-nowrap pl-2 text-right text-xs tabular-nums"
                    title={`${roundBookedWeeks(counts.total)} booked weeks (${roundBookedWeeks(counts.inSeason)} in season)`}
                  >
                    {roundBookedWeeks(counts.total)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="text-muted-foreground mt-4 flex flex-wrap items-center gap-3 text-xs">
          {LEGEND_ORDER.map((kind) => (
            <span key={kind} className="flex items-center gap-1.5">
              <span className={cn('h-3 w-3 rounded-[2px]', cellStyle(kind).className)} />
              {cellStyle(kind).label}
            </span>
          ))}
        </div>

        {/* The two prices in this grid come from different places and mean
            different things, and nothing on the cell says which is which: a
            booked week carries what that booking is worth, an open one what we
            are currently asking for it. */}
        <p className="text-muted-foreground mt-2 text-xs">
          A booked week shows the booking&rsquo;s final price. An open week shows the current asking
          price for that week, after discount. {NO_PRICE_MARK} means the week was sold but carries
          no price in MMK.
        </p>
      </CardContent>
    </Card>
  );
}
